import {
    secondLineText,
    secondLineTextContainer,
} from 'owa-mail-listitem-styles/lib/scss/MailListItem.scss';

import { observer } from 'owa-mobx-react';
import { highlightTermsInHtmlElement } from 'owa-highlight';
import {
    type ConditionalFormattingModification,
    getConditionalFormattingStyles,
    ConditionalFormattingSurface,
} from 'owa-conditional-formatting';
import React from 'react';
import type { Accuracy } from 'owa-mark/lib/utils/markElements';

export interface MailListItemLineTextProps {
    addOverflowStyles: boolean;
    highlightTerms: string[];
    text: string;
    tooltipText?: string;
    isDarkTheme: boolean;
    additionalStyles?: { [style: string]: string };
    highlightAccuracy?: Accuracy;
    conditionalFormattingModifications?: ConditionalFormattingModification[];
    isSelected?: boolean;
}

const defaultExport: React.FunctionComponent<MailListItemLineTextProps> = observer(
    function MailListItemLineText(props: MailListItemLineTextProps): React.JSX.Element {
        const {
            addOverflowStyles,
            additionalStyles,
            highlightTerms: _highlightTerms,
            text,
            tooltipText,
            highlightAccuracy,
            conditionalFormattingModifications,
            isDarkTheme,
            isSelected = false,
        } = props;

        const textStyle = React.useMemo(() => {
            const conditionalFormattingStyles =
                conditionalFormattingModifications && conditionalFormattingModifications.length > 0
                    ? getConditionalFormattingStyles(
                          ConditionalFormattingSurface.Text,
                          conditionalFormattingModifications,
                          isDarkTheme,
                          isSelected
                      )
                    : undefined;

            let baseStyles: React.CSSProperties = {};

            if (conditionalFormattingStyles) {
                const baseColor = conditionalFormattingStyles.color;
                baseStyles = {
                    ...conditionalFormattingStyles,
                    // Use CSS variable pattern so --cfHoverTextColorOverride from container is applied on hover
                    color: baseColor ? `var(--cfHoverTextColorOverride, ${baseColor})` : undefined,
                } as React.CSSProperties;
            }

            if (additionalStyles) {
                return {
                    ...baseStyles,
                    ...additionalStyles,
                };
            }
            return baseStyles;
        }, [additionalStyles, conditionalFormattingModifications, isDarkTheme, isSelected]);
        const hasTextStyle = Object.keys(textStyle).length > 0;

        const textRef = React.useRef<HTMLSpanElement>(null);
        React.useEffect(() => {
            highlightTermsInHtmlElement(
                textRef.current,
                _highlightTerms,
                false /* separateWordSearch */,
                highlightAccuracy /* highlightAccuracy */
            );
        }, [_highlightTerms, highlightAccuracy, text, hasTextStyle]);

        return hasTextStyle ? (
            <div style={textStyle} className={secondLineTextContainer}>
                <span
                    className={addOverflowStyles ? secondLineText : undefined}
                    title={tooltipText || undefined}
                    ref={textRef}
                >
                    {text}
                </span>
            </div>
        ) : (
            <span
                className={addOverflowStyles ? secondLineText : undefined}
                title={tooltipText || undefined}
                ref={textRef}
            >
                {text}
            </span>
        );
    },
    'MailListItemLineText'
);
export default defaultExport;
